const { Type } = require('@sinclair/typebox');
const { moduleRegistration } = require('@sstdev/lib_metadata-config');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const arrayFromCommaSeparatedValues = require('../../../../helpers/arrayFromCommaSeparatedValues');
const convertToBoolean = require('../../../../helpers/convertToBoolean');

const TOOLTIP =
    'Select one or more files from the local file system, using file selection dialog drag-drop, or taking photos';

function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged horizontally (this is the default)
    //Meaning each (dummy)input continues on the top line followed by  the access modifiers
    blocklyBlock.setInputsInline(false);
    line1OfInputs.appendField('  Property Name:').appendField(new Blockly.FieldTextInput('file'), 'propertyName');
    line1OfInputs
        .appendField('  Tooltip:')
        .appendField(new Blockly.FieldTextInput('Click to select, or drag & drop file to upload.'), 'tooltip');

    //line 2 of inputs
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Supported File Extensions (comma separated):')
        .appendField(new Blockly.FieldTextInput('.csv, .xlsx, .xls'), 'mimeTypes')
        .appendField('  Allow Multiple Files :')
        .appendField(new Blockly.FieldCheckbox('false'), 'allowMultiple')
        .appendField('  Allow Taking Photos :')
        .appendField(new Blockly.FieldCheckbox('false'), 'allowTakingPictures');
}

function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    addCodeProperties([
        'propertyName',
        { key: 'mimeTypes', handler: arrayFromCommaSeparatedValues },
        { key: 'allowMultiple', handler: convertToBoolean },
        { key: 'allowTakingPictures', handler: convertToBoolean }
    ]);
    //nested blocks in the statements are added automatically by blockly
    return code;
}

/** Builds the TypeBox schema fragment for the FilePicker form element. */
function asDataDictionary(hNode, context) {
    const { id, title, propertyName, allowMultiple, required } = hNode;

    // This block tells us nothing about namespaces/relations
    const subDataDictionary = {};

    // Determine required/optional/editable flags based on the block configuration
    const { isEditable, isOptional, schemaOptions } = moduleRegistration.resolveFieldFlags(required, context);
    const optional = isOptional;
    const editable = isEditable;

    // Helper to wrap a schema as optional if needed
    const maybeOptional = schema => (optional ? Type.Optional(schema) : schema);

    // Build the file object schema with name, size, and type properties
    const fileObjectSchema = Type.Object(
        {
            name: Type.String({
                title: 'File Name',
                'x-propertyName': 'name',
                'x-dataType': 'string',
                'x-optional': !!optional,
                'x-editable': !!editable
            }),
            size: Type.Number({
                title: 'File Size (Bytes)',
                'x-propertyName': 'size',
                'x-dataType': 'number',
                'x-optional': !!optional,
                'x-editable': !!editable
            }),
            type: Type.String({
                title: 'File Type',
                'x-propertyName': 'type',
                'x-dataType': 'string',
                'x-optional': !!optional,
                'x-editable': !!editable
            })
        },
        {
            title: allowMultiple ? 'File' : title,
            'x-dataType': 'object',
            // additionalProperties: true allows extending file objects with extra metadata
            // This enables adding properties like lastModified, content, etc. without breaking validation
            additionalProperties: true
        }
    );

    // If allowMultiple is true, wrap the file object in an array
    let fileSchema;
    if (allowMultiple) {
        fileSchema = maybeOptional(
            Type.Array(fileObjectSchema, {
                title: title,
                'x-id': id,
                'x-dataType': 'array',
                'x-propertyName': propertyName,
                ...schemaOptions
            })
        );
    } else {
        fileSchema = maybeOptional(
            Type.Object(
                {
                    name: Type.String({
                        title: 'File Name',
                        'x-propertyName': 'name',
                        'x-dataType': 'string',
                        'x-optional': !!optional,
                        'x-editable': !!editable
                    }),
                    size: Type.Number({
                        title: 'File Size (Bytes)',
                        'x-propertyName': 'size',
                        'x-dataType': 'number',
                        'x-optional': !!optional,
                        'x-editable': !!editable
                    }),
                    type: Type.String({
                        title: 'File Type',
                        'x-propertyName': 'type',
                        'x-dataType': 'string',
                        'x-optional': !!optional,
                        'x-editable': !!editable
                    })
                },
                {
                    title: title,
                    'x-id': id,
                    'x-dataType': 'object',
                    'x-propertyName': propertyName,
                    ...schemaOptions,
                    // additionalProperties: true allows extending file objects with extra metadata
                    // This enables adding properties like lastModified, content, etc. without breaking validation
                    // For example, a file object could be extended with a "content" property containing the file's contents as a base64-encoded string
                    additionalProperties: true
                }
            )
        );
    }

    // The partial type definition is a plain object with the property schema
    // This maps the propertyName to the generated file schema for validation
    const partialTypeDefinition = {
        [propertyName]: fileSchema
    };

    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
